/**
 * Decide which SicBo bet ids "win" given a parsed dice result.
 * Mirrors the resolution logic in
 *   packages/amgsicbo_v2/lib/core/utils/result_resolver.dart (or inline in bloc).
 *
 * Used by ChipLayer.animateWinningChips to fly winning chips to player
 * and losing chips to dealer.
 */
import type { ParsedResult } from './resultParser';
import { countOf, hasCombo, isEven, isOdd } from './resultParser';

export function winningBetIds(r: ParsedResult): Set<number> {
  const out = new Set<number>();

  // Triples disqualify Small/Big/Odd/Even (standard SicBo rule).
  if (!r.isTriple) {
    if (r.total >= 4 && r.total <= 10) out.add(1); // Small
    if (r.total >= 11 && r.total <= 17) out.add(2); // Big
    if (isOdd(r)) out.add(3);
    if (isEven(r)) out.add(4);
  }

  // Total 4..17 — only valid when total falls in that range.
  if (r.total >= 4 && r.total <= 17) {
    out.add(5 + (r.total - 4));
  }

  // Singles 1..6 — win if value appears at least once.
  for (let v = 1; v <= 6; v++) {
    if (countOf(r, v) > 0) out.add(18 + v); // 19..24
  }

  // Doubles 1..6 — win if value appears at least twice.
  for (let v = 1; v <= 6; v++) {
    if (countOf(r, v) >= 2) out.add(24 + v); // 25..30
  }

  // Triples 1..6 — win if all three dice equal v.
  if (r.isTriple) {
    out.add(30 + r.dice1); // 31..36
    out.add(37); // Any Triple
  }

  // Combos 1-2 .. 5-6 (15 pairs, ids 38..52).
  const PAIRS: Array<[number, number, number]> = [
    [38, 1, 2], [39, 1, 3], [40, 1, 4], [41, 1, 5], [42, 1, 6],
    [43, 2, 3], [44, 2, 4], [45, 2, 5], [46, 2, 6],
    [47, 3, 4], [48, 3, 5], [49, 3, 6],
    [50, 4, 5], [51, 4, 6],
    [52, 5, 6],
  ];
  for (const [id, a, b] of PAIRS) {
    if (hasCombo(r, a, b)) out.add(id);
  }

  return out;
}

/** Short text label for a dice round (used in game log). */
export function formatDiceResult(r: ParsedResult): string {
  const dice = `${r.dice1}-${r.dice2}-${r.dice3} (total ${r.total})`;
  if (r.isTriple) return `Triple ${r.dice1} — ${dice}`;
  if (r.category === 'small') return `Small — ${dice}`;
  return `Big — ${dice}`;
}
